import React, { useState, useRef, useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import { FolderOpen, Folder, FileText, Image, FileSignature, StickyNote, ArrowLeft, ShieldCheck, MessageCircle, Send, Printer, Stethoscope, PenLine, FilePlus2, Layers, Lock, NotebookPen, Syringe } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'
// Shared with PatientShow's Visits & Chats tab — one row component, so the two pages showing a
// signed pack can never drift apart. See app/javascript/components/SignedPackCard.jsx.
import SignedPackCard from '../components/SignedPackCard'

const DOC_ICON = { image: Image, xray: Image, form: FileSignature, note: StickyNote, pdf: FileText, file: FileText }

// Render saved pen strokes (vector JSON) read-only — handwritten notes shown inline beside typed text.
function InkNote({ ink }) {
  const ref = useRef(null)
  useEffect(() => {
    let strokes = []
    try { strokes = JSON.parse(ink) } catch { return }
    if (!Array.isArray(strokes) || !strokes.length) return
    const cv = ref.current; if (!cv) return
    let maxX = 200, maxY = 40
    strokes.forEach((s) => (s.pts || []).forEach((p) => { if (p[0] > maxX) maxX = p[0]; if (p[1] > maxY) maxY = p[1] }))
    cv.width = Math.ceil(maxX + 24); cv.height = Math.ceil(maxY + 24)
    const cx = cv.getContext('2d'); cx.lineCap = 'round'; cx.lineJoin = 'round'
    cx.fillStyle = '#fffdf7'; cx.fillRect(0, 0, cv.width, cv.height)
    strokes.forEach((s) => {
      cx.strokeStyle = s.color || '#1f2937'
      for (let i = 1; i < (s.pts || []).length; i++) {
        const a = s.pts[i - 1], b = s.pts[i]
        cx.beginPath(); cx.lineWidth = (s.size || 3) * (0.5 + (b[2] || 0.5)); cx.moveTo(a[0], a[1]); cx.lineTo(b[0], b[1]); cx.stroke()
      }
    })
  }, [ink])
  return <canvas ref={ref} className="mt-1 max-w-full rounded border border-brand-border" />
}
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// Charting lives in the patient's file, beside the folders it fills (Paul 2026-08-04) — the
// two ways in are the two things she actually does at the chair: start a brand-new chart, or
// carry on from the last one. "Continue" never copies the old ink forward; it links to it and
// shows it faintly underneath, the way a recall is charted on paper.
function ChartingCard({ patient, charting = {} }) {
  const { open_sessions = 0, last_signed = null, latest_date = null, signed_count = 0 } = charting
  const go = (mode) => { window.location.href = `/patients/${patient.id}/write?mode=${mode}` }

  return (
    <div className="mb-4 rounded-xl border border-brand-border bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <PenLine size={16} className="text-brand-primary" />
        <h2 className="text-sm font-semibold text-brand-ink">Charting</h2>
        <span className="ml-auto text-xs text-brand-muted">
          {signed_count > 0 ? `${signed_count} signed` : 'none signed yet'}
          {open_sessions > 0 ? ` · ${open_sessions} open` : ''}
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <button
          onClick={() => go('fresh')}
          className="flex items-start gap-3 rounded-lg border border-brand-border bg-white p-3 text-left hover:border-brand-primary hover:bg-brand-surface/50"
        >
          <FilePlus2 size={18} className="mt-0.5 shrink-0 text-brand-primary" />
          <span>
            <span className="block text-sm font-semibold text-brand-ink">Start a fresh chart</span>
            <span className="block text-xs text-brand-muted">A blank chart and treatment plan for this appointment.</span>
          </span>
        </button>

        <button
          onClick={() => go('continue')}
          disabled={!latest_date}
          className="flex items-start gap-3 rounded-lg border border-brand-border bg-white p-3 text-left hover:border-brand-primary hover:bg-brand-surface/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Layers size={18} className="mt-0.5 shrink-0 text-brand-primary" />
          <span>
            <span className="block text-sm font-semibold text-brand-ink">Continue on the latest chart</span>
            <span className="block text-xs text-brand-muted">
              {latest_date
                ? `Carries on from ${fmtDate(latest_date)} — shown faintly underneath, never copied.`
                : 'Nothing charted yet.'}
            </span>
          </span>
        </button>
      </div>

      {last_signed && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-brand-muted">
          <Lock size={12} className="text-emerald-600" />
          Last signed off by <b className="font-semibold text-brand-ink">{last_signed.by}</b> on {last_signed.at}
          {last_signed.document_id ? ' · filed under Charting (signed)' : ''}
        </p>
      )}
    </div>
  )
}

export default function PatientFile({ patient = {}, folders = [], forms = [], signed_pack: signedPack = null, notes = [], charting = {}, clinical_enabled = false }) {
  const [open, setOpen] = useState(folders.find((f) => f.count > 0)?.key || null)
  const [sending, setSending] = useState(false)

  // The pack is printable as soon as a form EXISTS, not only once it is completed (Paul 2026-09-16).
  // IntakePdf now pre-fills an unsubmitted form from the patient's autosaved draft, so a patient who
  // is mid-form when they arrive gets a pack with their own answers already on it to check and sign.
  const hasPrintablePack = forms.length > 0

  const sendIntake = () => {
    if (!window.confirm(`Send the intake forms link to ${patient.name} on WhatsApp?`)) return
    setSending(true)
    router.post(`/patients/${patient.id}/send-intake`, {}, {
      preserveScroll: true,
      onFinish: () => setSending(false),
    })
  }

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> Patient
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <FolderOpen size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">{patient.name} — Digital File</h1>
          <p className="text-sm text-brand-muted">Paperless patient file — replaces the physical folders</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {clinical_enabled && (
            <Link
              href={`/patients/${patient.id}/clinical-notes`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:brightness-95"
            >
              <NotebookPen size={15} /> Clinical notes
            </Link>
          )}
          {clinical_enabled && (
            <Link
              href={`/patients/${patient.id}/aesthetic-records`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-surface"
            >
              <Syringe size={15} /> Aesthetics
            </Link>
          )}
          <Link
            href={`/patients/${patient.id}/referrals/new`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-surface"
          >
            <Stethoscope size={15} /> New referral
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Folders + documents */}
        <div className="lg:col-span-2">
          <ChartingCard patient={patient} charting={charting} />
          <div className="space-y-2">
            {folders.map((f) => {
              const isOpen = open === f.key
              return (
                <div key={f.key} className="overflow-hidden rounded-xl border border-brand-border bg-white">
                  <button onClick={() => setOpen(isOpen ? null : f.key)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-brand-surface/50">
                    <Folder size={16} className="text-brand-muted" />
                    <span className="text-sm font-medium text-brand-ink">{f.label}</span>
                    <span className="ml-auto text-xs text-brand-muted">{f.count}</span>
                  </button>
                  {isOpen && f.documents.length > 0 && (
                    <div className="border-t border-brand-border">
                      {f.documents.map((d) => {
                        const Icon = DOC_ICON[d.doc_type] || FileText
                        return (
                          <a key={d.id} href={`/documents/${d.id}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-3 border-b border-brand-border/50 px-4 py-2 last:border-0 hover:bg-brand-surface/50">
                            <Icon size={15} className="text-brand-muted" />
                            <span className="text-sm text-brand-ink">{d.title}</span>
                            {d.signed && <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700"><ShieldCheck size={11} /> Signed</span>}
                            <span className="ml-auto text-xs text-brand-muted">{d.source.replace('_', ' ')} · {fmtDate(d.captured_at)}</span>
                          </a>
                        )
                      })}
                    </div>
                  )}
                  {isOpen && f.documents.length === 0 && (
                    <div className="border-t border-brand-border px-4 py-3 text-xs text-brand-muted">Empty folder.</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Forms + notepad */}
        <div className="space-y-6">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-brand-ink"><MessageCircle size={15} /> WhatsApp forms</h2>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={sendIntake}
                  disabled={sending}
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-primary px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-60"
                >
                  <Send size={12} /> {sending ? 'Sending…' : 'Send intake'}
                </button>
                {hasPrintablePack && (
                  <a
                    href={`/patients/${patient.id}/intake.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg border border-brand-border bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-ink-soft hover:bg-brand-surface"
                  >
                    <Printer size={12} /> Print pack
                  </a>
                )}
              </div>
            </div>
            {/* The signed pack first — it is the thing the desk acts on. The send/open history below
                is just status per form; the signature evidence lives on the card, once. */}
            {signedPack && <div className="mb-2"><SignedPackCard pack={signedPack} compact /></div>}
            <div className="rounded-xl border border-brand-border bg-white">
              {forms.length === 0 && <p className="px-4 py-3 text-xs text-brand-muted">No forms sent.</p>}
              {forms.map((s) => (
                <div key={s.id} className="flex items-center gap-2 border-b border-brand-border/50 px-4 py-2 last:border-0">
                  <span className="text-sm text-brand-ink">{s.name}</span>
                  {s.signed && <ShieldCheck size={12} className="text-emerald-600" title={`Signed ${s.signed_at}`} />}
                  <span className={cn('ml-auto rounded px-1.5 py-0.5 text-[11px]', s.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700')}>{s.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-ink"><StickyNote size={15} /> Notepad</h2>
            <div className="rounded-xl border border-brand-border bg-white">
              {notes.length === 0 && <p className="px-4 py-3 text-xs text-brand-muted">No notes.</p>}
              {notes.map((n) => (
                <div key={n.id} className="border-b border-brand-border/50 px-4 py-2 last:border-0">
                  <p className="text-sm font-medium text-brand-ink">{n.title}</p>
                  {n.content ? <p className="whitespace-pre-wrap text-xs text-brand-muted">{n.content}</p> : null}
                  {n.ink ? <InkNote ink={n.ink} /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
